import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { PRIVACIDAD, RESUMEN_PRIVACIDAD } from '../config/privacidad';
import { NOMBRE_APP, FECHA_PRIVACIDAD } from '../config/marca';
import { agrupar } from '../utils/texto';

/**
 * Política de privacidad. Pública (sin iniciar sesión): Apple pide su URL en
 * App Store Connect y tiene que poder leerse antes de crear la cuenta.
 * El contenido vive en src/config/privacidad.ts.
 */
export const Privacidad: React.FC = () => {
  const { user } = useAuth();
  const { hash } = useLocation();

  // /privacidad#derechos baja hasta esa sección
  useEffect(() => {
    const id = decodeURIComponent(hash.replace('#', ''));
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [hash]);

  const bloques = (parrafos: string[]) => agrupar(parrafos).map((b, i) => Array.isArray(b) ? (
    <ul key={i} className="list-disc pl-5 space-y-1">
      {b.map((li, j) => <li key={j}>{li}</li>)}
    </ul>
  ) : <p key={i}>{b}</p>);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="sticky top-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-b border-gray-200
                         dark:border-gray-700 seguro-arriba">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Logo size={28} />
          <span className="font-bold text-gray-900 dark:text-white flex-1">Privacidad</span>
          <Link to={user ? '/' : '/login'}
            className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 dark:text-blue-400 hover:underline">
            <ArrowLeft className="w-4 h-4" /> {user ? 'Volver a la app' : 'Entrar'}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 seguro-abajo">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Política de privacidad de {NOMBRE_APP}</h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Última actualización: {FECHA_PRIVACIDAD}</p>

        <div className="mt-5 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/70 dark:bg-blue-900/20 p-4
                        text-sm text-gray-800 dark:text-gray-200 space-y-2">
          <p className="flex items-center gap-2 font-semibold text-blue-800 dark:text-blue-300">
            <ShieldCheck className="w-4 h-4" /> En pocas palabras
          </p>
          {bloques(RESUMEN_PRIVACIDAD)}
        </div>

        <nav className="flex flex-wrap gap-2 mt-5" aria-label="Secciones">
          {PRIVACIDAD.map(s => (
            <a key={s.id} href={`#${s.id}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200
                         dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400">
              {s.titulo}
            </a>
          ))}
        </nav>

        {PRIVACIDAD.map(s => (
          <section key={s.id} id={s.id} className="mt-8 scroll-mt-20">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{s.titulo}</h2>
            <div className="mt-2 space-y-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              {bloques(s.parrafos)}
            </div>
          </section>
        ))}

        <p className="mt-10 mb-4 text-center text-xs text-gray-500 dark:text-gray-400">
          <Link to="/ayuda" className="hover:underline">Ver la ayuda</Link>
        </p>
      </main>
    </div>
  );
};
